import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import BombiixAGGrid, { ServerSortHeader } from '../../../Components/BombiixAGGrid';
import type { BombiixColumnDef, BombiixRow } from '../../../Components/BombiixAGGrid';
import { NewCheckbox } from '../../../Components/PIM/common/NewCheckbox';
import { NewInput } from '../../../Components/PIM/common/NewInput';
import { NewSelectMultiSelect } from '../../../Components/PIM/common/NewSelectMultiSelect';
import type { NewSelectMultiSelectOption } from '../../../Components/PIM/common/NewSelectMultiSelect/NewSelectMultiSelect';
import { NewToggle } from '../../../Components/PIM/common/NewToggle';
import { EditIcon, DeleteIcon, MoreIcon, CloseIcon, SaveIcon, TwoFAIcon, SetPasswordIcon, ResendPasswordIcon } from '../../../bombiix-icon';
import { AssignmentDraft, AssignmentField, AssignmentsByUser, AssignmentValue, toPickedValues } from './user-assignment.types';
import { filterSubBrandsByBrand, type SubBrandOption } from './user-assignment.types';

/**
 * A supplier user row as the grid renders it.
 */
export type SupplierUserRow = BombiixRow & {
  _id?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phoneNo?: string;
  accountVerified?: boolean;
  hasPassword?: boolean;
  hasSsoLink?: boolean;
};

/**
 * The row under edit: the user's text fields plus the four assignment grants.
 */
export type UserDraftRow = Record<string, unknown> & AssignmentDraft;

type DraftChangeHandler = (field: string, value: string | AssignmentValue) => void;

type UserColumnDef = BombiixColumnDef<SupplierUserRow> & { cellRendererParams?: unknown };

interface SelectAllHeaderProps {
  checked: boolean;
  disabled: boolean;
  label: string;
  onToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface SelectRowCellProps {
  data?: SupplierUserRow;
  selectedIdArray: Array<string | undefined> | undefined;
  label: string;
  onToggle: (event: React.ChangeEvent<HTMLInputElement>, id: string | undefined) => void;
}

interface EditableTextCellProps {
  data?: SupplierUserRow;
  field: string;
  placeholder: string;
  isRowEditing: boolean;
  draftRow: UserDraftRow | null | undefined;
  onDraftChange: DraftChangeHandler;
  required: boolean;
}

interface AssignmentCellProps {
  data?: SupplierUserRow;
  assignmentsByUser: AssignmentsByUser | undefined;
  field: AssignmentField;
  allLabel: string;
  options: NewSelectMultiSelectOption[] | undefined;
  isRowEditing: boolean;
  draftRow: UserDraftRow | null | undefined;
  onDraftChange: DraftChangeHandler;
  selectPlaceholder: string;
  searchPlaceholder: string;
  chipVariant: string;
}

interface SortTableState {
  sortPropertyName?: string;
  sortOrder?: string;
}

export interface UserStatusChange {
  user_id: string | undefined;
  user_status: boolean | undefined;
}

interface UserTableProps {
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  allUsers: SupplierUserRow[] | undefined;
  assignmentsByUser: AssignmentsByUser | undefined;
  brandOptions: NewSelectMultiSelectOption[] | undefined;
  subBrandOptions: SubBrandOption[] | undefined;
  category1Options: NewSelectMultiSelectOption[] | undefined;
  category2Options: NewSelectMultiSelectOption[] | undefined;
  draftRow: UserDraftRow | null | undefined;
  editingUserId: string | null | undefined;
  onDraftChange: DraftChangeHandler;
  onSaveRow: () => void;
  onCancelRow: () => void;
  onStartEdit?: (userId: string | undefined) => void;
  onResetMfa: (userId: string | undefined, email: string | undefined) => void;
  onOpenSetPassword: (user?: SupplierUserRow) => void;
  onSelectCheckBox: (event: React.ChangeEvent<HTMLInputElement>, id: string | undefined) => void;
  isSelectAll: boolean;
  selectedIdArray: Array<string | undefined> | undefined;
  onDelete: (userId: string | undefined) => void;
  onEdit: (event: null, userId: string | undefined) => void;
  onEditStatus: (event: React.ChangeEvent<HTMLInputElement>, change: UserStatusChange) => void;
  onSortColumn: (field: string) => void;
  sortTable: SortTableState | undefined;
  onSetPassword: (userId: string | undefined, email: string | undefined) => void;
  CostingDeleteAccess: boolean;
  CostingAddEditAccess: boolean;
}

/**
 * Header checkbox that selects or clears every user on the page.
 * @param {object} params - AG Grid header params plus `checked`, `disabled`, `label`, `onToggle`.
 * @returns {React.ReactElement} The header checkbox.
 */
function SelectAllHeader({ checked, disabled, label, onToggle }: SelectAllHeaderProps) {
  return <NewCheckbox id="check_all" name="check_all" checked={checked} disabled={disabled} aria-label={label} onChange={onToggle} />;
}

/**
 * Per-row selection checkbox.
 * @param {object} params - AG Grid cell params plus `selectedIdArray`, `label`, `onToggle`.
 * @returns {React.ReactElement} The row checkbox.
 */
function SelectRowCell({ data, selectedIdArray, label, onToggle }: SelectRowCellProps) {
  return (
    <NewCheckbox
      id={`checks_${data?._id}`}
      name={`checks_${data?._id}`}
      checked={(selectedIdArray ?? []).filter((val) => val === data?._id).length > 0}
      aria-label={label}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggle(e, data?._id)}
    />
  );
}

const DRAFT_ROW_ID = '__draft__';

/**
 * Text cell that switches to an input while its row is being edited.
 * Validation shows while the row is being edited rather than only on save, so
 * a required column that still needs filling is obvious before the save is
 * attempted. Email additionally has to look like an address.
 * @param {object} params - AG Grid cell params plus the draft wiring.
 * @returns {React.ReactElement} The cell.
 */
function EditableTextCell({ data, field, placeholder, isRowEditing, draftRow, onDraftChange, required }: EditableTextCellProps) {
  if (!isRowEditing) return <span>{String(data?.[field] ?? '')}</span>;
  const value = String(draftRow?.[field] ?? '');
  const isBlank = required && !String(value).trim();
  const isBadEmail = field === 'email' && String(value).trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());
  return (
    <NewInput
      value={value}
      placeholder={placeholder}
      invalid={isBlank || isBadEmail}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onDraftChange(field, event.target.value)}
      aria-label={placeholder}
    />
  );
}

/**
 * Assignment cell — the Figma "Status Selection Dropdown" (node 14091:302404).
 *
 * One 28px control holds everything: the all-access tick, a hairline divider,
 * the granted values as chips, then the chevron. The control renders on every
 * row, not just the one being edited — outside edit mode it is simply disabled,
 * so a row keeps the same shape whether you are reading it or changing it.
 *
 * "All access" is the assignment's `true` sentinel, which the design collapses
 * to one chip rather than listing every option.
 *
 * AG Grid clips its cells and rows, so a menu rendered inside one is cut off at
 * the row edge. The menu is portalled to the body to escape that, and `fixed`
 * keeps it anchored while the grid scrolls horizontally.
 * @param {object} params - AG Grid cell params plus the draft wiring.
 * @returns {React.ReactElement} The cell.
 */
function AssignmentCell({
  data,
  assignmentsByUser,
  field,
  allLabel,
  options,
  isRowEditing,
  draftRow,
  onDraftChange,
  selectPlaceholder,
  searchPlaceholder,
  chipVariant,
}: AssignmentCellProps) {
  const stored = assignmentsByUser?.[data?._id ?? '']?.[field] ?? { isAll: false, labels: [] };
  const draftValue = draftRow?.[field];
  const isAll = isRowEditing ? draftValue === true : stored.isAll;

  const selected = isRowEditing
    ? (options ?? []).filter((option) => (Array.isArray(draftValue) ? draftValue.map(String).includes(String(option?.value)) : false))
    : stored.labels.map((label) => ({ label, value: label }));

  const value = isAll ? [{ label: allLabel, value: '__all__' }] : selected;

  return (
    <div className="user-assignment-cell">
      <NewCheckbox
        checked={isAll}
        disabled={!isRowEditing}
        aria-label={allLabel}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onDraftChange(field, event.target.checked ? true : [])}
      />
      <span className="user-assignment-cell__divider" aria-hidden="true" />
      <span className="user-assignment-cell__control">
        <NewSelectMultiSelect
          isMulti
          options={isAll ? [] : options ?? []}
          value={value}
          isDisabled={!isRowEditing || isAll}
          placeholder={selectPlaceholder}
          containerClassName={`user-assignment-select user-assignment-select--${chipVariant}`}
          showSearch
          searchPlaceholder={searchPlaceholder}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          onChange={(picked) => onDraftChange(field, toPickedValues(picked))}
        />
      </span>
    </div>
  );
}

/**
 * Supplier users listing. The grid is read-only: every column renders a value
 * and the row's actions live in the pinned trailing column, so no cell is
 * editable and `onRowChange` is never wired.
 * Sorting stays with the caller — `onSortColumn` refetches the page — so the
 * sortable columns carry `ServerSortHeader` and are declared `sortable: false`
 * to stop AG Grid reordering the fetched page on top of the server's answer.
 *
 * The actions column is 144px, not the Figma frame's 120px: that frame is drawn
 * against 20px glyphs on 4px gaps, where 28 + 20 + 20 + 20 + 3 gaps = 100px sits
 * exactly inside a 10px inset either side. At 24px glyphs on the shared 8px gap
 * the same four actions measure 28 + 24 + 24 + 24 + 3 gaps = 124px, so the cell
 * needs 124 + 10 + 10. At 140 the content overflowed the padded box by 4px and,
 * being right-aligned, spilled leftward.
 * @param {object} props - Component props.
 * @returns {React.ReactElement} The users grid.
 */
function UserTable({
  handleSelectAll,
  allUsers,
  assignmentsByUser,
  brandOptions,
  subBrandOptions,
  category1Options,
  category2Options,
  draftRow,
  editingUserId,
  onDraftChange,
  onSaveRow,
  onCancelRow,
  onStartEdit,
  onResetMfa,
  onOpenSetPassword,
  onSelectCheckBox,
  isSelectAll,
  selectedIdArray,
  onDelete,
  onEdit,
  onEditStatus,
  onSortColumn,
  sortTable,
  onSetPassword,
  CostingDeleteAccess,
  CostingAddEditAccess,
}: UserTableProps) {
  const { t } = useTranslation('common');

  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const handleToggleAction = (rowId: string | undefined, nextShow: boolean) => {
    setOpenPopoverId(nextShow ? rowId ?? null : null);
  };

  const sortHeaderParams = useMemo(
    () => ({
      activeSortField: sortTable?.sortPropertyName,
      activeSortOrder: sortTable?.sortOrder,
      onSort: onSortColumn,
    }),
    [sortTable?.sortPropertyName, sortTable?.sortOrder, onSortColumn],
  );

  /**
   * True when this row is the one under edit — either the pinned draft entry row
   * or the existing row whose pencil was clicked.
   * @param {object} row - The grid row.
   * @returns {boolean} Whether the row renders its editing controls.
   */
  const isRowInEditMode = (row: SupplierUserRow | undefined): boolean => {
    if (!draftRow) return false;
    return row?._id === DRAFT_ROW_ID || Boolean(editingUserId && row?._id === editingUserId);
  };

  /**
   * Builds a server-sorted column whose cell flips to an input while its row is
   * the one being edited (or is the draft entry row).
   * @param {string} field - Record field, which doubles as the sort key.
   * @param {string} headerName - Column title.
   * @param {number} width - Column width, per the Figma header row.
   * @param {string} placeholder - Placeholder shown in edit mode.
   * @param {boolean} required - Whether the edit input flags a blank value.
   * @returns {object} A Bombiix column definition.
   */
  const sortedColumn = (field: string, headerName: string, width: number, placeholder?: string, required = false): UserColumnDef => ({
    field,
    headerName,
    width,
    sortable: false,
    required,
    headerComponent: ServerSortHeader,
    headerComponentParams: { ...sortHeaderParams, sortField: field },
    cellRenderer: EditableTextCell,
    cellRendererParams: (params: { data?: SupplierUserRow }) => ({
      field,
      placeholder: placeholder ?? headerName,
      isRowEditing: isRowInEditMode(params?.data),
      draftRow,
      onDraftChange,
      required,
    }),
  });

  const columnDefs = useMemo(() => {
    const columns: UserColumnDef[] = [];
    if (CostingAddEditAccess) {
      columns.push({
        colId: 'select',
        headerName: '',
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        headerComponent: SelectAllHeader,
        headerComponentParams: {
          checked: isSelectAll,
          disabled: !allUsers?.length,
          label: t('COMMON_LABEL.SELECT_ALL'),
          onToggle: handleSelectAll,
        },
        cellRenderer: SelectRowCell,
        cellRendererParams: {
          selectedIdArray,
          label: t('BTN_FILTER.SELECT'),
          onToggle: onSelectCheckBox,
        },
      });
    }
    columns.push(sortedColumn('firstName', t('USER_TABLE.FIRST_NAME'), 200, undefined, true));
    columns.push(sortedColumn('lastName', t('USER_TABLE.LAST_NAME'), 200, undefined, true));
    columns.push(sortedColumn('title', t('USER_TABLE.JOB_TITLE'), 200, undefined, true));
    columns.push(sortedColumn('email', t('USER_TABLE.EMAIL'), 300, undefined, true));
    columns.push(sortedColumn('phoneNo', t('USER_TABLE.PHONE_NUMBER'), 200));

    /**
     * Brand / Sub Brand / Category 1 / Category 2 are the rights assignment, not
     * fields on the user record — the page resolves them to labels and hands
     * them down keyed by user id. 200px each, matching the Figma header row.
     * There is no Status column: the Figma header row (node 14091:302321) ends at
     * Category 2, and the account's enabled state is already the toggle that
     * leads the row actions.
     * @param {AssignmentField} field - The grant this column shows.
     * @param {string} headerName - Column title.
     * @param {NewSelectMultiSelectOption[] | undefined} options - The grant's choices.
     * @param {string} chipVariant - Chip colour modifier.
     * @returns {UserColumnDef} The column definition.
     */
    const assignmentColumn = (
      field: AssignmentField,
      headerName: string,
      options: NewSelectMultiSelectOption[] | undefined,
      chipVariant: string,
    ): UserColumnDef => ({
      colId: field,
      headerName,
      width: 200,
      sortable: false,
      headerComponent: ServerSortHeader,
      headerClass: 'partner-users-static-header',
      cellRenderer: AssignmentCell,
      cellRendererParams: (params: { data?: SupplierUserRow }) => ({
        assignmentsByUser,
        field,
        allLabel: t('USER_TABLE.FULL_ACCESS'),
        options,
        selectPlaceholder: t('BTN_FILTER.SELECT'),
        searchPlaceholder: t('COMMON_LABEL.SEARCH'),
        chipVariant,
        isRowEditing: isRowInEditMode(params?.data),
        draftRow,
        onDraftChange,
      }),
    });
    columns.push(assignmentColumn('brand', t('USER_TABLE.BRAND'), brandOptions, 'brand'));
    columns.push(assignmentColumn('subBrand', t('USER_TABLE.SUB_BRAND'), filterSubBrandsByBrand(subBrandOptions, draftRow?.brand), 'sub-brand'));
    columns.push(assignmentColumn('category1', t('USER_TABLE.CATEGORY_1'), category1Options, 'category'));
    columns.push(assignmentColumn('category2', t('USER_TABLE.CATEGORY_2'), category2Options, 'category-2'));
    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    CostingAddEditAccess,
    isSelectAll,
    allUsers,
    selectedIdArray,
    sortHeaderParams,
    assignmentsByUser,
    draftRow,
    editingUserId,
    brandOptions,
    subBrandOptions,
    category1Options,
    category2Options,
    t,
  ]);

  /**
   * Edit / delete / overflow menu for one row, rendered in the pinned actions column.
   *
   * A row being edited trades its normal actions for confirm / discard, so the
   * entry row and an edited row end the same way (Figma node 12385:163422).
   * Account enable/disable and "resend email" sit on the row itself, not in the
   * overflow menu: both are one-click states a reviewer scans down the column.
   * Resend sits between Edit and the overflow (Figma node 14091:302558) and is
   * only offered while the user has neither a password nor an SSO link.
   *
   * The overflow holds Reset 2FA · Set Password · Delete, per the Figma row menu.
   * Reset 2FA and Set Password only apply once the account is live. That is read
   * from `hasPassword`, not `accountVerified`: `addSupplierUser` sends
   * `accountVerified: true` in the create payload, so it is true from the moment
   * the row is saved, whereas `hasPassword` flips when the invited user completes
   * setup — the complement of the Resend Email condition, so the two never show at once.
   * @param {object} params - AG Grid cell params for the actions column.
   * @returns {React.ReactNode} The row's actions.
   */
  const renderRowActionExtras = (params: { data?: SupplierUserRow | undefined }) => {
    const item = params?.data;
    if (!item) return null;

    if (isRowInEditMode(item)) {
      return (
        <>
          <button className="bombiix-icon-container" type="button" aria-label={t('COMMON_LABEL.SAVE')} title={t('COMMON_LABEL.SAVE')} onClick={onSaveRow}>
            <SaveIcon size={24} />
          </button>
          <button className="bombiix-icon-container" type="button" aria-label={t('COMMON_LABEL.CLOSE')} title={t('COMMON_LABEL.CLOSE')} onClick={onCancelRow}>
            <CloseIcon size={24} />
          </button>
        </>
      );
    }

    return (
      <>
        {CostingAddEditAccess && (
          <NewToggle
            id={`user-status-${item._id}`}
            checked={Boolean(item.accountVerified)}
            aria-label={item.accountVerified ? t('USER_TABLE.ENABLED') : t('USER_TABLE.DISABLED')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEditStatus(e, { user_id: item._id, user_status: item.accountVerified })}
          />
        )}
        {CostingAddEditAccess && (
          <button
            className="bombiix-icon-container"
            aria-label="edit"
            type="button"
            title={t('USER_TABLE.EDIT')}
            onClick={() => (onStartEdit ? onStartEdit(item._id) : onEdit(null, item._id))}
          >
            <EditIcon size={24} />
          </button>
        )}
        {CostingAddEditAccess && !item.hasPassword && !item.hasSsoLink && (
          <button
            className="bombiix-icon-container"
            aria-label="resend email"
            type="button"
            title={t('USER_TABLE.RESEND_EMAIL')}
            onClick={() => onSetPassword(item._id, item.email)}
          >
            <ResendPasswordIcon size={24} />
          </button>
        )}

        {CostingAddEditAccess && (
          <OverlayTrigger
            placement="left"
            trigger="click"
            rootClose
            onToggle={(nextShow: boolean) => handleToggleAction(item._id, nextShow)}
            overlay={
              <Popover id={`user-actions-${item._id}`} className="table-action-popup bombiix-action-menu">
                <Popover.Content>
                  <ul>
                    {item.hasPassword && (
                      <li>
                        <button type="button" className="btn d-flex align-items-center" onClick={() => onResetMfa(item._id, item.email)}>
                          <TwoFAIcon size={24} />
                          <span className="text ml-8">{t('COMMON.RESET_2FA_ACCOUNT')}</span>
                        </button>
                      </li>
                    )}
                    {item.hasPassword && (
                      <li>
                        <button type="button" className="btn d-flex align-items-center" onClick={() => onOpenSetPassword(item)}>
                          <SetPasswordIcon size={24} />
                          <span className="text ml-8">{t('COMMON.SET_PASSWORD')}</span>
                        </button>
                      </li>
                    )}
                    {CostingDeleteAccess && (
                      <li>
                        <button type="button" className="btn d-flex align-items-center" onClick={() => onDelete(item._id)}>
                          <DeleteIcon size={24} />
                          <span className="text ml-8">{t('USER_TABLE.DELETE')}</span>
                        </button>
                      </li>
                    )}
                  </ul>
                </Popover.Content>
              </Popover>
            }
          >
            <button type="button" className="bombiix-icon-container" aria-label="more actions">
              {openPopoverId === item._id ? <CloseIcon size={24} /> : <MoreIcon size={24} />}
            </button>
          </OverlayTrigger>
        )}
      </>
    );
  };

  const hasRowActions = CostingAddEditAccess || CostingDeleteAccess;

  const isDraftNew = Boolean(draftRow) && !editingUserId;
  const rowData: SupplierUserRow[] = isDraftNew ? [{ _id: DRAFT_ROW_ID }, ...(allUsers ?? [])] : allUsers ?? [];

  return (
    <BombiixAGGrid<SupplierUserRow>
      rowData={rowData}
      columnDefs={columnDefs}
      getRowId={(row) => row?._id ?? ''}
      readOnly
      sortable={false}
      rowActionsWidth={144}
      rowActionsClassName="user-row-actions"
      {...(hasRowActions ? { renderRowActionExtras } : {})}
    />
  );
}

export default UserTable;
